import { useRef, useState } from "react";
import { Phone, Home, ImagePlus, X, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { usePageMeta } from "../hooks/usePageMeta";
import { api, formatApiErrorDetail } from "../lib/api";
import { SITE, IMAGES } from "../data/site";

const SITUATIONS = [
  "Inherited home that needs to be emptied",
  "Parent or relative moving to senior living",
  "Property being prepared for sale",
  "Homeowner already moved and left belongings behind",
  "Family does not want or need an estate sale",
  "Post-estate-sale remaining contents",
  "Downsizing",
  "Heavily cluttered property where MacWay's confirmed scope allows the work",
];

const CAN_HANDLE = [
  "Furniture and household goods",
  "Unwanted belongings",
  "Bagged and ordinary household trash",
  "Loading and hauling",
  "Donation coordination where appropriate",
  "Recycling coordination where appropriate",
  "Basic sorting under the agreed scope",
  "Final clearing of agreed areas",
];

const STEPS = [
  { n: "1", title: "Tell Us About the Property", text: "A short phone call or online request is enough to get started." },
  { n: "2", title: "On-Site Walkthrough", text: "We review what stays, what goes, access, volume, and any special handling." },
  { n: "3", title: "Clear Written Estimate", text: "MacWay provides the agreed scope and price before work begins." },
  { n: "4", title: "Cleanout", text: "Our crew removes the agreed contents and completes the property clearing." },
];

const PRICING_FACTORS = [
  "Amount and volume of contents",
  "Number of workers required",
  "Stairs and access",
  "Heavy or difficult items",
  "Number of loads",
  "Disposal costs",
  "Sorting required",
  "Donation or recycling routing",
  "Special handling",
];

const FAQS = [
  {
    q: "Do I need to sort everything before calling?",
    a: "No. MacWay can discuss what stays, what goes, and what is still being decided before work begins.",
  },
  {
    q: "Do you buy furniture or household items?",
    a: "MacWay provides paid cleanout and removal services. We do not purchase individual household items through this service.",
  },
  {
    q: "Do I need an estate sale first?",
    a: "No. A cleanout does not require an estate sale.",
  },
  {
    q: "Do I have to send photos?",
    a: "No. Photos are optional.",
  },
  {
    q: "Can you coordinate donations?",
    a: "Yes, when suitable items are accepted by available organizations. Donation acceptance is not guaranteed.",
  },
  {
    q: "Do you provide on-site estimates?",
    a: "Yes.",
  },
];

const CLEANOUT_SERVICES = [
  "Whole-House Cleanout",
  "Estate / Inherited Property Cleanout",
  "Downsizing Cleanout",
  "Apartment / Condo Cleanout",
  "Post-Estate-Sale Cleanout",
  "Furniture / Unwanted Contents Removal",
  "Other",
];

const URGENCY_OPTIONS = ["As soon as possible", "Within the next few weeks", "Within 1–2 months", "Flexible / planning ahead"];

const SIZE_OPTIONS = ["Apartment / condo", "Small home", "Medium home", "Large home", "Not sure"];

const initialForm = {
  name: "",
  phone: "",
  email: "",
  property_location: "",
  service_needed: "",
  urgency: "",
  property_size: "",
  description: "",
};

function CleanoutForm() {
  const [form, setForm] = useState(initialForm);
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [honeypot, setHoneypot] = useState("");
  const [startedAt] = useState(() => Date.now());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const fileInputRef = useRef(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image — skipped.`);
        continue;
      }
      const data = new FormData();
      data.append("file", file);
      try {
        const res = await api.post("/quotes/photos", data, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { ...res.data, preview: URL.createObjectURL(file) }]);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      }
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post("/quotes", {
        ...form,
        form_type: "cleanout",
        preferred_contact: form.phone ? "Phone" : "Email",
        photos: photos.map(({ path, original_filename, content_type }) => ({ path, original_filename, content_type })),
        website: honeypot,
        form_started_at: startedAt,
      });
      setDone(true);
      if (typeof window.gtag === "function") {
        // GA4: fires only after the request is saved — never includes personal details
        window.gtag("event", "cleanout_estimate_request", { method: "cleanout_form" });
      }
      document.getElementById("cleanout-estimate")?.scrollIntoView({ behavior: "smooth" });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="bg-white border border-linen p-10 md:p-14 text-center" data-testid="cleanout-success">
        <CheckCircle2 className="w-14 h-14 text-forest mx-auto" />
        <h3 className="text-3xl font-medium text-ink mt-6">Request received</h3>
        <p className="mt-4 text-ink/70 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
          Thank you. MacWay will contact you to discuss the property and the next step.
        </p>
        <p className="mt-6 text-sm text-ink/60">
          Prefer to talk now? Call{" "}
          <a href={SITE.phoneHref} className="font-semibold text-forest hover:underline">{SITE.phone}</a>.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="bg-white border border-linen p-7 md:p-10" data-testid="cleanout-form">
      <div style={{ position: "absolute", left: "-9999px", height: 0, overflow: "hidden" }} aria-hidden="true">
        <label htmlFor="cf-website">Website</label>
        <input id="cf-website" type="text" tabIndex={-1} autoComplete="off" value={honeypot} onChange={(e) => setHoneypot(e.target.value)} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label htmlFor="cf-name" className="form-label">Name <span className="text-brass">*</span></label>
          <input id="cf-name" className="form-input" required autoComplete="name" value={form.name} onChange={set("name")} data-testid="cleanout-form-name" />
        </div>
        <div>
          <label htmlFor="cf-location" className="form-label">Property city or ZIP <span className="text-brass">*</span></label>
          <input id="cf-location" className="form-input" required placeholder="e.g. Easton or 21601" value={form.property_location} onChange={set("property_location")} data-testid="cleanout-form-location" />
        </div>
        <div>
          <label htmlFor="cf-phone" className="form-label">Phone <span className="font-normal text-ink/50">(phone or email required)</span></label>
          <input id="cf-phone" type="tel" className="form-input" autoComplete="tel" value={form.phone} onChange={set("phone")} data-testid="cleanout-form-phone" />
        </div>
        <div>
          <label htmlFor="cf-email" className="form-label">Email <span className="font-normal text-ink/50">(phone or email required)</span></label>
          <input id="cf-email" type="email" className="form-input" autoComplete="email" value={form.email} onChange={set("email")} data-testid="cleanout-form-email" />
        </div>
        <div>
          <label htmlFor="cf-service" className="form-label">Service needed <span className="text-brass">*</span></label>
          <select id="cf-service" className="form-input" required value={form.service_needed} onChange={set("service_needed")} data-testid="cleanout-form-service">
            <option value="" disabled>Select a service…</option>
            {CLEANOUT_SERVICES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="cf-urgency" className="form-label">How soon do you need help? <span className="font-normal text-ink/50">(optional)</span></label>
          <select id="cf-urgency" className="form-input" value={form.urgency} onChange={set("urgency")} data-testid="cleanout-form-urgency">
            <option value="">Select…</option>
            {URGENCY_OPTIONS.map((u) => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="cf-size" className="form-label">Approximate property size <span className="font-normal text-ink/50">(optional)</span></label>
          <select id="cf-size" className="form-input" value={form.property_size} onChange={set("property_size")} data-testid="cleanout-form-size">
            <option value="">Select…</option>
            {SIZE_OPTIONS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="md:col-span-2">
          <label htmlFor="cf-notes" className="form-label">Brief notes <span className="font-normal text-ink/50">(optional)</span></label>
          <textarea id="cf-notes" rows={3} className="form-input resize-y" placeholder="A sentence or two is plenty." value={form.description} onChange={set("description")} data-testid="cleanout-form-notes" />
        </div>

        <div className="md:col-span-2">
          <span className="form-label">Photos <span className="font-normal text-ink/50">(optional)</span></span>
          <p className="text-xs text-ink/55 mb-3" data-testid="cleanout-photos-note">
            Photos are optional. You can speak with us and arrange an on-site estimate without sending photos.
          </p>
          <div className="flex flex-wrap items-center gap-4">
            {photos.map((ph) => (
              <div key={ph.path} className="relative w-20 h-20 border border-linen overflow-hidden group">
                <img src={ph.preview} alt={ph.original_filename || "Uploaded photo"} className="w-full h-full object-cover" />
                <button type="button" onClick={() => setPhotos((p) => p.filter((x) => x.path !== ph.path))} className="absolute top-1 right-1 bg-ink/70 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" aria-label="Remove photo" data-testid="cleanout-photo-remove">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="w-20 h-20 border-2 border-dashed border-linen hover:border-forest text-ink/50 hover:text-forest flex flex-col items-center justify-center gap-1 transition-colors disabled:opacity-50" data-testid="cleanout-photo-add">
              {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ImagePlus className="w-5 h-5" />}
              <span className="text-[0.6rem] font-semibold uppercase tracking-wide">{uploading ? "Uploading" : "Add photos"}</span>
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid="cleanout-photo-input" />
          </div>
        </div>
      </div>

      {error && (
        <p className="mt-6 text-sm font-semibold text-red-700 bg-red-50 border border-red-200 px-4 py-3" role="alert" data-testid="cleanout-form-error">
          {error}
        </p>
      )}

      <div className="mt-8 flex flex-col sm:flex-row items-center gap-5">
        <button type="submit" disabled={submitting || uploading} className="btn-brass w-full sm:w-auto disabled:opacity-60" data-testid="cleanout-form-submit">
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Sending…
            </>
          ) : (
            "Request a Cleanout Estimate"
          )}
        </button>
        <p className="text-xs text-ink/50 leading-relaxed">
          No full address or inventory needed — just the city or ZIP. Prefer to talk?{" "}
          <a href={SITE.phoneHref} className="font-semibold text-forest hover:underline">{SITE.phone}</a>
        </p>
      </div>
    </form>
  );
}

export default function EstateCleanoutsPage() {
  usePageMeta(
    "Estate & Whole-Home Cleanouts | MacWay Estate Services — MD & Northern VA",
    "Paid whole-home and estate cleanouts across Maryland and Northern Virginia. We empty houses, apartments, and condos — furniture, household goods, and trash removed. Call (301) 500-0128 or request a cleanout estimate."
  );

  return (
    <main data-testid="estate-cleanouts-page">
      {/* 1. Hero */}
      <section className="relative py-24 md:py-32 flex items-center" data-testid="cleanout-hero">
        <img src={IMAGES.cleanout} alt="A MacWay cleanout — garage before and after" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/30" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <p className="eyebrow-label" data-testid="cleanout-eyebrow">Paid home & estate cleanouts</p>
            <h1 className="mt-4 text-5xl sm:text-6xl font-semibold tracking-tight text-ivory leading-[1.05]" data-testid="cleanout-headline">
              Need the House Emptied? We Handle It.
            </h1>
            <p className="mt-6 text-lg text-ivory/85 leading-relaxed">
              MacWay helps families, executors, homeowners, and realtors clear houses, apartments, and condos.
              We remove furniture, household goods, ordinary trash, and unwanted contents whether or not
              anything is valuable enough for an estate sale.
            </p>
            <p className="mt-4 text-base text-ivory/75 leading-relaxed">
              This is a paid professional cleanout service. Tell us where the property is and what needs to be
              cleared. Photos are optional, and on-site estimates are available.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a href="#cleanout-estimate" className="btn-brass" data-testid="cleanout-hero-estimate-cta">
                Request a Cleanout Estimate
              </a>
              <a href={SITE.phoneHref} className="btn-outline-light" data-testid="cleanout-hero-call-cta">
                <Phone className="w-4 h-4" />
                Call MacWay {SITE.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 2. When a cleanout can help */}
      <section className="py-16 md:py-24 bg-ivory" data-testid="cleanout-situations">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">When a cleanout can help</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              Situations we can help with
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {SITUATIONS.map((s) => (
              <div key={s} className="bg-white border border-linen p-6 flex items-start gap-3" data-testid={`situation-${s.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}>
                <Home className="w-5 h-5 text-brass shrink-0 mt-0.5" />
                <p className="text-sm font-semibold text-ink/80 leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. What MacWay can handle */}
      <section className="py-16 md:py-24 bg-sand" data-testid="cleanout-scope">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="eyebrow-label">Scope</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              What a MacWay cleanout can include
            </h2>
            <p className="mt-5 text-base sm:text-lg text-ink/70 leading-relaxed">
              Every cleanout is scoped and priced in writing before work begins. An agreed cleanout may include:
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {CAN_HANDLE.map((item) => (
              <li key={item} className="bg-white border border-linen px-5 py-4 flex items-start gap-3 text-sm text-ink/75">
                <span className="mt-1.5 w-2 h-2 bg-brass shrink-0" aria-hidden="true" />
                {item}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 4. How it works */}
      <section className="py-16 md:py-24 bg-ivory" data-testid="cleanout-process">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <p className="eyebrow-label">How it works</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">Four straightforward steps</h2>
          </div>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((s) => (
              <div key={s.n} className="bg-white border border-linen p-7" data-testid={`cleanout-step-${s.n}`}>
                <span className="font-serif text-5xl font-semibold text-brass">{s.n}</span>
                <h3 className="mt-4 text-xl font-medium text-ink">{s.title}</h3>
                <p className="mt-2.5 text-sm text-ink/65 leading-relaxed">{s.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 bg-forest text-ivory p-8 md:p-10 max-w-4xl" data-testid="cleanout-reassurance">
            <p className="font-serif text-xl sm:text-2xl leading-relaxed">
              &ldquo;You do not need to sort the entire home before you call. Tell us what must stay and what is
              still being decided. We will agree on a plan before work begins.&rdquo;
            </p>
          </div>
        </div>
      </section>

      {/* 5. Pricing factors */}
      <section className="py-16 md:py-24 bg-sand" data-testid="cleanout-pricing">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <p className="eyebrow-label">Pricing</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">
              What shapes a cleanout price
            </h2>
            <p className="mt-5 text-base sm:text-lg text-ink/70 leading-relaxed">
              Every property is different, so we don't publish a one-size price. You receive a clear written
              estimate before work begins — and on-site estimates are available.
            </p>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {PRICING_FACTORS.map((f) => (
              <li key={f} className="bg-white border border-linen px-5 py-4 text-sm text-ink/75 flex items-start gap-3">
                <span className="mt-1.5 w-2 h-2 bg-forest shrink-0" aria-hidden="true" />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* 6. Estimate form */}
      <section className="py-16 md:py-24 bg-ivory scroll-mt-24" id="cleanout-estimate" data-testid="cleanout-form-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="eyebrow-label">Get started</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">Request a cleanout estimate</h2>
            <p className="mt-4 text-base text-ink/70 leading-relaxed max-w-xl mx-auto">
              Short on purpose — name, how to reach you, the city or ZIP, and what needs clearing.
            </p>
          </div>
          <CleanoutForm />
        </div>
      </section>

      {/* 7. FAQ */}
      <section className="py-16 md:py-24 bg-sand" data-testid="cleanout-faq">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="eyebrow-label">Questions</p>
            <h2 className="mt-4 text-4xl sm:text-5xl font-medium tracking-tight text-ink">Cleanout FAQ</h2>
          </div>
          <div className="space-y-4">
            {FAQS.map((f) => (
              <div key={f.q} className="bg-white border border-linen p-6" data-testid={`faq-${f.q.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 30)}`}>
                <h3 className="font-serif text-xl font-medium text-ink">{f.q}</h3>
                <p className="mt-2 text-sm text-ink/70 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
          <p className="mt-10 text-center text-sm text-ink/60">
            Still have a question? Call{" "}
            <a href={SITE.phoneHref} className="font-semibold text-forest hover:underline" data-testid="cleanout-faq-call">{SITE.phone}</a>{" "}
            — a real person will help you sort out the next step.
          </p>
        </div>
      </section>
    </main>
  );
}
